import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { Mail, Download, Megaphone, Bell, Trash2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from 'recharts';
import { toast } from 'sonner';

const StatCard = ({ label, value, color, onClick }) => (
  <div
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    className={`bg-white rounded-xl p-4 sm:p-5 border border-gray-100 ${onClick ? 'cursor-pointer hover:border-[#1800ad]/40 hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#1800ad]/20' : ''}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <p className="text-[11px] sm:text-xs text-[#6B7280] font-medium uppercase tracking-wide">{label}</p>
    <p className="text-2xl sm:text-3xl font-black mt-1" style={{ color, fontFamily: 'Google Sans Flex' }}>{value}</p>
  </div>
);

const AdminOverview = ({ onNavigateTab }) => {
  const [stats, setStats] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [series, setSeries] = useState([]);
  const [topCats, setTopCats] = useState([]);
  const [revByPlan, setRevByPlan] = useState([]);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [bcSubject, setBcSubject] = useState('');
  const [bcBody, setBcBody] = useState('');
  const [bcSegment, setBcSegment] = useState('all');
  const [bcSending, setBcSending] = useState(false);
  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [annUrl, setAnnUrl] = useState('/');
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [s, e, t, c, r, a] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getEmailStatus().catch(() => ({ data: {} })),
        adminAPI.analyticsTimeseries(30).catch(() => ({ data: [] })),
        adminAPI.analyticsTopCategories().catch(() => ({ data: [] })),
        adminAPI.analyticsRevenueByPlan().catch(() => ({ data: [] })),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements`).then(r => r.json()).catch(() => []),
      ]);
      setStats(s.data); setEmailStatus(e.data); setSeries(t.data);
      setTopCats(c.data); setRevByPlan(r.data); setAnnouncements(a);
    } catch (err) { toast.error('Failed to load overview'); }
  };

  const sendTest = async () => {
    if (!testEmailTo || !testEmailTo.includes('@')) { toast.error('Enter a valid email'); return; }
    setTestSending(true);
    try {
      const res = await adminAPI.sendTestEmail(testEmailTo);
      if (res.data.sent) toast.success(`Test email sent from ${res.data.from_address}`);
      else toast.error(res.data.reason || 'Not configured');
    } catch (e) { toast.error('Failed'); } finally { setTestSending(false); }
  };

  const sendBroadcast = async () => {
    if (!bcSubject.trim() || !bcBody.trim()) { toast.error('Enter subject and body'); return; }
    const highRisk = bcSegment === 'all' || bcSegment === 'recent_signups';
    let confirmToken = '';
    if (highRisk) {
      confirmToken = window.prompt(`This will email the "${bcSegment}" segment. Type SEND to confirm:`, '');
      if (!confirmToken || confirmToken.toUpperCase() !== 'SEND') { toast.error('Cancelled'); return; }
    } else {
      if (!window.confirm(`Send "${bcSubject}" to "${bcSegment}" segment?`)) return;
    }
    setBcSending(true);
    try {
      const res = await adminAPI.emailBroadcast(bcSubject, bcBody, bcSegment, confirmToken);
      toast.success(`Broadcast sent: ${res.data.sent} delivered, ${res.data.failed} failed (of ${res.data.total} recipients)`);
      setBcSubject(''); setBcBody('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBcSending(false); }
  };

  const createAnnouncement = async () => {
    if (!annTitle.trim() || !annBody.trim()) { toast.error('Title and body required'); return; }
    try {
      await adminAPI.createAnnouncement(annTitle, annBody, annUrl || '/');
      toast.success('Announcement posted to all users');
      setAnnTitle(''); setAnnBody(''); setAnnUrl('/');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try { await adminAPI.deleteAnnouncement(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  if (!stats) return <div className="text-center py-12 text-sm text-[#9CA3AF]">Loading…</div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-grid">
        {/* iter134 — clickable stat cards → jump straight to the matching tab. */}
        <StatCard label="Users"       value={stats.total_users}         color="#1800ad" onClick={onNavigateTab ? () => onNavigateTab('users')    : undefined} />
        <StatCard label="Listings"    value={stats.total_listings}      color="#ff8500" onClick={onNavigateTab ? () => onNavigateTab('listings') : undefined} />
        <StatCard label="Active Subs" value={stats.total_subscriptions} color="#10B981" onClick={onNavigateTab ? () => onNavigateTab('payments') : undefined} />
        <StatCard label="Reports"     value={stats.total_reports}       color="#F59E0B" onClick={onNavigateTab ? () => onNavigateTab('reports')  : undefined} />
      </div>

      {/* Time series */}
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
        <h3 className="text-sm sm:text-base font-bold text-[#0A0B10] mb-3">Last 30 Days</h3>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="signups" stroke="#1800ad" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="listings" stroke="#ff8500" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="payments" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top categories */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
          <h3 className="text-sm font-bold text-[#0A0B10] mb-3">Top Categories (by active listings)</h3>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={topCats} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1800ad" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue by purpose */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
          <h3 className="text-sm font-bold text-[#0A0B10] mb-3">Revenue by Purpose</h3>
          {revByPlan.length === 0 ? (
            <p className="text-xs text-[#9CA3AF] py-6 text-center">No captured payments yet</p>
          ) : (
            <div className="space-y-2 text-xs sm:text-sm">
              {revByPlan.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-[#f8f9fa] rounded">
                  <span className="truncate text-[#4B5563]">{r.purpose}</span>
                  <span className="font-bold text-[#0A0B10]">₹{r.total.toLocaleString()} <span className="text-[10px] text-[#9CA3AF]">({r.count})</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Email Diagnostics */}
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100" data-testid="email-diagnostics">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={16} className="text-[#1800ad]" />
          <h3 className="text-sm font-bold text-[#0A0B10]">Email Diagnostics</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs">
          <div className="bg-[#f8f9fa] rounded-lg px-3 py-2">From: <strong>{emailStatus?.from_address || '—'}</strong></div>
          <div className="bg-[#f8f9fa] rounded-lg px-3 py-2">SMTP: <strong className={emailStatus?.configured ? 'text-[#10B981]' : 'text-[#DC2626]'}>{emailStatus?.configured ? 'Configured' : 'Not configured (preview)'}</strong></div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="email" data-testid="test-email-input" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)}
            placeholder="recipient@example.com" className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
          <button data-testid="send-test-email-btn" onClick={sendTest} disabled={testSending}
            className="bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50">
            {testSending ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>

      {/* Email Broadcast */}
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100" data-testid="broadcast-card">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone size={16} className="text-[#ff8500]" />
          <h3 className="text-sm font-bold text-[#0A0B10]">Email Broadcast</h3>
        </div>
        <input value={bcSubject} onChange={(e) => setBcSubject(e.target.value)} placeholder="Subject"
          data-testid="bc-subject" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm mb-2 outline-none focus:border-[#1800ad]" />
        <textarea value={bcBody} onChange={(e) => setBcBody(e.target.value)} placeholder="HTML body" rows={4}
          data-testid="bc-body" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm mb-2 outline-none focus:border-[#1800ad]" />
        <div className="flex flex-col sm:flex-row gap-2">
          <select value={bcSegment} onChange={(e) => setBcSegment(e.target.value)} data-testid="bc-segment"
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm flex-1">
            <option value="all">All users with email</option>
            <option value="active_subscribers">Active subscribers only</option>
            <option value="recent_signups">Recent signups (30 days)</option>
          </select>
          <button onClick={sendBroadcast} disabled={bcSending} data-testid="bc-send"
            className="bg-[#ff8500] hover:bg-[#E67520] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50">
            {bcSending ? 'Sending…' : 'Send Broadcast'}
          </button>
        </div>
      </div>

      {/* In-app announcements */}
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100" data-testid="announcement-card">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={16} className="text-[#1800ad]" />
          <h3 className="text-sm font-bold text-[#0A0B10]">In-app Announcements</h3>
        </div>
        <input value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="Announcement title"
          data-testid="ann-title" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm mb-2 outline-none focus:border-[#1800ad]" />
        <textarea value={annBody} onChange={(e) => setAnnBody(e.target.value)} placeholder="Body" rows={2}
          data-testid="ann-body" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm mb-2 outline-none focus:border-[#1800ad]" />
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input value={annUrl} onChange={(e) => setAnnUrl(e.target.value)} placeholder="/ (link)"
            data-testid="ann-url" className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#1800ad]" />
          <button onClick={createAnnouncement} data-testid="ann-create"
            className="bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-lg px-4 py-2 text-sm font-bold">Post</button>
        </div>
        {announcements.length > 0 && (
          <div className="space-y-2 mt-3">
            {announcements.map((a) => (
              <div key={a.announcement_id} className="flex items-center justify-between p-2 bg-[#f8f9fa] rounded text-xs">
                <div className="min-w-0">
                  <p className="font-bold text-[#0A0B10] truncate">{a.title}</p>
                  <p className="text-[#6B7280] truncate">{a.body}</p>
                </div>
                <button onClick={() => deleteAnnouncement(a.announcement_id)} className="p-1.5 rounded text-red-600 hover:bg-red-50">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSV Exports */}
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Download size={16} className="text-[#1800ad]" />
          <h3 className="text-sm font-bold text-[#0A0B10]">CSV Exports</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={adminAPI.exportUsersCsv()} target="_blank" rel="noreferrer"
            className="bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white rounded-lg px-4 py-2 text-xs sm:text-sm font-bold transition-colors">
            Download Users CSV
          </a>
          <a href={adminAPI.exportListingsCsv()} target="_blank" rel="noreferrer"
            className="bg-[#fff4ed] text-[#ff8500] hover:bg-[#ff8500] hover:text-white rounded-lg px-4 py-2 text-xs sm:text-sm font-bold transition-colors">
            Download Listings CSV
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
